import { useState } from 'react';

import { Stack } from '@mui/material';

import { tokens } from '../../../../../theme/generated/tokens';
import { SxProps } from '../../../../../types';
import { mergeSx } from '../../../../../utils';
import Button from '../../../../buttons/Button';
import { Chip } from '../../../../chip';
import * as Icons from '../../../../icons';
import { dropdownClassNames } from '../../classNames';

type ChipDisplayProps<Item> = {
  selectedItems: Item[];
  minShownChips: number;
  itemToStringLabel: (item: Item) => string;
  onRemoveItem: (item: Item) => void;
  prefixOutside?: React.ReactNode;
  chipSx?: SxProps;
  readOnly?: boolean;
};

export const ChipDisplay = <Item,>({
  selectedItems,
  minShownChips,
  itemToStringLabel,
  onRemoveItem,
  prefixOutside,
  chipSx,
  readOnly = false,
}: ChipDisplayProps<Item>) => {
  const [chipsExpanded, setChipsExpanded] = useState(false);

  const getSelectedChipItems = (): Item[] => {
    if (chipsExpanded) {
      return selectedItems;
    }
    return selectedItems.filter((_, index) => index < minShownChips);
  };

  const getNoOfSelectedChipItemsHidden = (): number => {
    const selectedChipItems = getSelectedChipItems();
    return selectedItems.length - selectedChipItems.length;
  };

  const handleChipRemove = (item: Item): void => {
    onRemoveItem(item);
  };

  const handleToggleExpanded = (): void => {
    setChipsExpanded(!chipsExpanded);
  };

  const selectedChipItems = getSelectedChipItems();
  const noOfSelectedChipItemsHidden = getNoOfSelectedChipItemsHidden();
  const shouldShowExpandButton = selectedItems.length > minShownChips;

  if (selectedChipItems.length === 0) {
    return;
  }

  return (
    <Stack
      gap="8px"
      flexWrap="wrap"
      flexDirection="row"
      alignItems="center"
      sx={{
        marginTop: '2px',
        gridColumnStart: prefixOutside ? 2 : 1,
      }}
      data-testid="selected-chips"
      className={dropdownClassNames.chipDisplay}
    >
      {selectedChipItems.map((item, index) => (
        <Chip
          testId={`selected-chip-${index}`}
          key={index}
          sx={mergeSx(
            {
              backgroundColor: tokens.theme.bg.selection.primaryHover,
              color: tokens.theme.fg.selection.onPrimary,
            },
            chipSx
          )}
          onDestroy={readOnly ? undefined : () => handleChipRemove(item)}
        >
          {itemToStringLabel(item)}
        </Chip>
      ))}

      {shouldShowExpandButton && (
        <Button
          variant="text"
          onClick={handleToggleExpanded}
          sx={{
            padding: '0px 8px',
          }}
          endIcon={chipsExpanded ? <Icons.CaretUp /> : undefined}
          startIcon={!chipsExpanded ? <Icons.Plus /> : undefined}
        >
          {chipsExpanded ? 'Show less' : `${noOfSelectedChipItemsHidden} more`}
        </Button>
      )}
    </Stack>
  );
};
